import type { TableColumn } from "@general-backoffice/core";
import { Table } from "@general-backoffice/core/index";
import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, CardHeader, Collapse } from "reactstrap";

import getCurrencySymbol from "../../utils/getCurrencySymbol";

const eventTypeColors: Record<string, string> = {
  STOP_LOSS: "danger",
  STOP_INSUFFICIENT_FUNDS: "danger",
  STOP_BALANCE_TOO_HIGH: "danger",
  STOP_LEVERAGE_TOO_LOW: "danger",
  STOP_LEVERAGE_TOO_HIGH: "danger",
  STOP_INVALID_CREDENTIALS: "danger",
  STOP_CREDIT_BONUS_RESTRICTED: "danger",
  LOW_MARGIN_WARNING: "warning",
  DRAWDOWN_WARNING: "warning",
};

// Format percentage - if value is decimal (< 1), multiply by 100
const formatPercent = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "--") return "--";
  const num = parseFloat(String(value));
  if (isNaN(num)) return "--";
  const percent = num < 1 && num > 0 ? num * 100 : num;
  return percent.toFixed(2);
};

interface BotEvent {
  timestamp?: string | number;
  createdAt?: string | null;
  type: string;
  details?: Record<string, unknown>;
  [key: string]: unknown;
}

interface BotEventsCardProps {
  events?: BotEvent[];
  isLoading?: boolean;
  defaultCollapsed?: boolean;
}

const BotEventsCard = ({ events = [], isLoading, defaultCollapsed = false }: BotEventsCardProps) => {
  const { t, i18n } = useTranslation("bots", { keyPrefix: "eventsHistoryCard" });
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  // Sort events by timestamp descending (newest first)
  const sortedEvents = [...events].sort((a, b) => {
    const dateA = new Date(a.timestamp || a.createdAt || 0);
    const dateB = new Date(b.timestamp || b.createdAt || 0);
    return dateB.getTime() - dateA.getTime();
  });

  // Format event details using translations
  const formatDetails = (type: string, details: Record<string, unknown> = {}) => {
    switch (type) {
      case "STOP_LOSS":
        return t("eventMessages.STOP_LOSS", {
          realizedLoss: details.realizedLoss || "--",
          balance: details.balance || "--",
        });
      case "STOP_INSUFFICIENT_FUNDS": {
        const accountType = details.contractSize === 100000 ? "standard" : "micro";
        const currencySymbol = getCurrencySymbol(String(details.currency || "EUR"));
        return t("eventMessages.STOP_INSUFFICIENT_FUNDS", {
          accountType: t(`accountTypes.${accountType}`),
          currencySymbol,
          minimumRequired: details.minimumRequired || "--",
          balance: details.balance || "--",
        });
      }
      case "STOP_BALANCE_TOO_HIGH":
        return t("eventMessages.STOP_BALANCE_TOO_HIGH", { balance: details.balance || "--" });
      case "STOP_LEVERAGE_TOO_LOW":
        return t("eventMessages.STOP_LEVERAGE_TOO_LOW", {
          leverage: details.leverage || "--",
          minLeverage: details.minLeverage || "--",
        });
      case "STOP_LEVERAGE_TOO_HIGH":
        return t("eventMessages.STOP_LEVERAGE_TOO_HIGH", {
          leverage: details.leverage || "--",
          maxLeverage: details.maxLeverage || "--",
        });
      case "STOP_INVALID_CREDENTIALS":
        return t("eventMessages.STOP_INVALID_CREDENTIALS");
      case "STOP_CREDIT_BONUS_RESTRICTED":
        return t("eventMessages.STOP_CREDIT_BONUS_RESTRICTED", { creditAmount: details.creditAmount || "--" });
      case "LOW_MARGIN_WARNING":
        return t("eventMessages.LOW_MARGIN_WARNING");
      case "DRAWDOWN_WARNING":
        return t("eventMessages.DRAWDOWN_WARNING", {
          drawdown: formatPercent(details.drawdown != null ? String(details.drawdown) : undefined),
        });
      default:
        return "-";
    }
  };

  const columns: TableColumn[] = [
    {
      name: t("headers.date"),
      formattedContent: (event) => {
        const timestamp = event.timestamp || event.createdAt;
        const mm = moment(timestamp).locale(i18n.language);
        return (
          <div className="my--3">
            <div className="font-weight-500">{mm.format("HH:mm:ss").replaceAll(":", " : ")}</div>
            <small>{mm.format("L")}</small>
          </div>
        );
      },
    },
    {
      name: t("headers.type"),
      formattedContent: (row) => (
        <Badge color={eventTypeColors[row.type] || "secondary"}>
          {t(`eventTypes.${row.type}`, { defaultValue: String(row.type) })}
        </Badge>
      ),
    },
    {
      name: t("headers.details"),
      formattedContent: (row) => formatDetails(row.type, row.details),
    },
  ];

  return (
    <Card>
      <CardHeader
        className="d-flex justify-content-between align-items-center py-3"
        style={{ cursor: "pointer" }}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <h5 className="mb-0">
          <i className="fas fa-bell mr-2" />
          {t("title")}
          <Badge color="secondary" className="ml-2">
            {events.length}
          </Badge>
        </h5>
        <Button
          color="link"
          className="p-0 text-muted"
          aria-expanded={!isCollapsed}
          aria-label={isCollapsed ? t("expand") : t("collapse")}
          title={isCollapsed ? t("expand") : t("collapse")}
        >
          <i className={`fas fa-chevron-${isCollapsed ? "down" : "up"}`} aria-hidden="true" />
        </Button>
      </CardHeader>
      <Collapse isOpen={!isCollapsed}>
        <Table
          columns={columns}
          rows={sortedEvents}
          isLoading={isLoading}
          noContentMessage={t("empty")}
          rowHeight={60}
          rowAmount={5}
          selectable={false}
          sortable={false}
        />
      </Collapse>
    </Card>
  );
};

export default BotEventsCard;
